/* Crop a label photo before it is read.

   A pack photo holds far more than the panel we need: the brand, directions,
   a nutrition table beside the ingredients. Framing just the panel gives OCR
   less to misread and keeps other text out of the ingredient list. The crop
   box is dragged to move and resized from its corners, by finger or mouse. */

import { escapeHtml, html, qs } from "./dom";

type Box = { x: number; y: number; w: number; h: number };
const MIN = 40; // smallest crop box, in screen pixels

/** Let the user frame part of the photo. Resolves with the cropped photo, the
 *  whole photo ("Use whole photo"), or null when they cancel. */
export function cropPhoto(file: File, title: string): Promise<File | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const dialog = html<HTMLDialogElement>(`<dialog class="crop-dialog">
      <h2>${escapeHtml(title)}</h2>
      <p class="muted small">Drag the box over the part to read. Pull a corner to resize.</p>
      <div class="crop-stage"><div class="crop-frame">
        <img class="crop-img" alt="Photo to crop" src="${url}">
        <div class="crop-box" hidden>
          <span class="crop-h nw" data-h="nw"></span><span class="crop-h ne" data-h="ne"></span>
          <span class="crop-h sw" data-h="sw"></span><span class="crop-h se" data-h="se"></span>
        </div>
      </div></div>
      <div class="crop-actions">
        <button class="btn outline small" type="button" data-act="cancel">Cancel</button>
        <button class="btn soft small" type="button" data-act="whole">Use whole photo</button>
        <button class="btn primary small" type="button" data-act="crop">Use crop</button>
      </div>
    </dialog>`);
    document.body.appendChild(dialog);

    const img = qs<HTMLImageElement>(dialog, ".crop-img");
    const boxEl = qs(dialog, ".crop-box");
    let box: Box = { x: 0, y: 0, w: 0, h: 0 };

    const draw = (): void => {
      Object.assign(boxEl.style, { left: `${box.x}px`, top: `${box.y}px`, width: `${box.w}px`, height: `${box.h}px` });
    };
    const clamp = (b: Box): Box => {
      const W = img.clientWidth;
      const H = img.clientHeight;
      const w = Math.min(Math.max(b.w, MIN), W);
      const h = Math.min(Math.max(b.h, MIN), H);
      return { w, h, x: Math.min(Math.max(b.x, 0), W - w), y: Math.min(Math.max(b.y, 0), H - h) };
    };

    img.addEventListener("load", () => {
      // Start with a box inset 10% from each edge.
      const W = img.clientWidth;
      const H = img.clientHeight;
      box = clamp({ x: W * 0.1, y: H * 0.1, w: W * 0.8, h: H * 0.8 });
      boxEl.hidden = false;
      draw();
    });

    // One pointer drag at a time: move the box, or resize from a corner.
    let drag: { handle: string; startX: number; startY: number; from: Box } | null = null;
    boxEl.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      const handle = (event.target as HTMLElement).dataset.h ?? "move";
      drag = { handle, startX: event.clientX, startY: event.clientY, from: { ...box } };
      boxEl.setPointerCapture(event.pointerId);
    });
    boxEl.addEventListener("pointermove", (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const f = drag.from;
      const next: Box = { ...f };
      if (drag.handle === "move") {
        next.x = f.x + dx;
        next.y = f.y + dy;
      } else {
        if (drag.handle.includes("w")) { next.x = Math.min(f.x + dx, f.x + f.w - MIN); next.w = f.w - (next.x - f.x); }
        if (drag.handle.includes("e")) next.w = f.w + dx;
        if (drag.handle.includes("n")) { next.y = Math.min(f.y + dy, f.y + f.h - MIN); next.h = f.h - (next.y - f.y); }
        if (drag.handle.includes("s")) next.h = f.h + dy;
        // Keep the opposite corner where it was when a size limit is hit.
        next.x = Math.max(next.x, 0);
        next.y = Math.max(next.y, 0);
      }
      box = clamp(next);
      draw();
    });
    const end = (): void => {
      drag = null;
    };
    boxEl.addEventListener("pointerup", end);
    boxEl.addEventListener("pointercancel", end);

    const finish = (result: File | null): void => {
      dialog.close();
      dialog.remove();
      URL.revokeObjectURL(url);
      resolve(result);
    };

    const cropped = (): Promise<File | null> => {
      // Map the on-screen box back to the photo's own pixels.
      const scale = img.naturalWidth / img.clientWidth;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(box.w * scale);
      canvas.height = Math.round(box.h * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return Promise.resolve(file);
      ctx.drawImage(img, box.x * scale, box.y * scale, box.w * scale, box.h * scale, 0, 0, canvas.width, canvas.height);
      return new Promise((done) =>
        canvas.toBlob((blob) => {
          if (!blob) return done(file);
          const name = file.name.replace(/\.[^.]+$/, "") + "-crop.jpg";
          done(new File([blob], name, { type: "image/jpeg", lastModified: Date.now() }));
        }, "image/jpeg", 0.92),
      );
    };

    dialog.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((button) => {
      button.addEventListener("click", () => {
        const act = button.dataset.act;
        if (act === "cancel") finish(null);
        else if (act === "whole") finish(file);
        else void cropped().then(finish);
      });
    });
    // Esc closes a <dialog> natively: treat it as Cancel.
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      finish(null);
    });
    dialog.showModal();
  });
}
